import React, { useState } from 'react';
import Header from '../Common_Files/Header';
import Footer from '../Common_Files/Footer';
import config from '../Common_Files/Config';
import CustomerSuggestionBox from '../CommonComp/CustomerSuggestionBox';
import ViewCustomer from '../Master/ViewCustomer';
import { DateRangePicker } from 'rsuite';
import { format } from 'date-fns';

const parseFilterDate = (value) => {
    if (!value) return null;
    const parts = String(value).split('/');
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (!month || !day || !year) return null;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
};

const SalesLedger = () => {
    const accessToken = localStorage.getItem('accessToken');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [reportrange, setReportrange] = useState('');
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [periodLabel, setPeriodLabel] = useState('');
    const [ledgerRows, setLedgerRows] = useState([]);
    const [summary, setSummary] = useState({
        total_debit: '0.00',
        total_credit: '0.00',
        difference: '0.00',
        is_balanced: true,
    });

    const handleDateChange = (dates) => {
        if (dates && dates.length === 2) {
            const [start, end] = dates;
            setFromDate(format(start, 'MM/dd/yyyy'));
            setToDate(format(end, 'MM/dd/yyyy'));
            setReportrange(`${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`);
        } else if (!dates) {
            setFromDate('');
            setToDate('');
            setReportrange('');
        }
    };

    const handleSearch = async () => {
        if (!accessToken) {
            window.location.href = '/login';
            return;
        }
        setLoading(true);
        setMessage('');
        const formData = new FormData();
        formData.append('start_date', fromDate);
        formData.append('end_date', toDate);
        if (customer?.value) {
            formData.append('customer_id', customer.value);
        }
        try {
            const response = await fetch(`${config.api_url}/get_sales_account_ledger`, {
                method: 'POST',
                headers: { authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            const json = await response.json();
            if (!json.success || !json.data) {
                setLedgerRows([]);
                setSummary({
                    total_debit: '0.00',
                    total_credit: '0.00',
                    difference: '0.00',
                    is_balanced: true,
                });
                setPeriodLabel('');
                setMessage(json.message || 'No ledger entries found.');
                return;
            }
            const data = json.data;
            setLedgerRows(Array.isArray(data.ledger) ? data.ledger : []);
            setSummary({
                total_debit: data.summary?.total_debit || '0.00',
                total_credit: data.summary?.total_credit || '0.00',
                difference: data.summary?.difference || data.summary?.closing_balance || '0.00',
                is_balanced:
                    typeof data.summary?.is_balanced === 'boolean'
                        ? data.summary.is_balanced
                        : true,
            });
            setPeriodLabel(
                data.start_date && data.end_date ? `${data.start_date} - ${data.end_date}` : reportrange
            );
            setMessage(
                data.ledger && data.ledger.length > 0
                    ? ''
                    : 'No ledger entries for selected period.'
            );
        } catch {
            setMessage('Failed to load sales ledger');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <ViewCustomer />
            <div className="breadcrumbs">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 col-lg-12 col-sm-12">
                            <a href="#"><i className="fa fa-home"></i></a> &gt;{' '}
                            <a href="/Dashboard">Dashboard</a> &gt;{' '}
                            <span className="last-crumb">Sales Ledger</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="main-container">
                <div className="container-fluid">
                    <div className="show-order-area dashboard account-ledger-page">
                        <div className="account-ledger-toolbar">
                            <div className="dashform">
                                <div className="topsearch-area sales-ledger-filters">
                                    <div className="row g-2 g-lg-3 align-items-end">
                                        <div className="col-12 col-md-6 col-xl-3">
                                            <label className="col-form-label">Customer (optional)</label>
                                            <CustomerSuggestionBox onCustomerChange={setCustomer} />
                                        </div>
                                        <div className="col-12 col-md-6 col-xl-4">
                                            <label className="col-form-label">Date Range</label>
                                            <DateRangePicker
                                                format="dd/MM/yyyy"
                                                editable={false}
                                                cleanable
                                                placeholder="DD/MM/YYYY - DD/MM/YYYY"
                                                style={{ width: '100%' }}
                                                value={[
                                                    parseFilterDate(fromDate),
                                                    parseFilterDate(toDate),
                                                ]}
                                                onChange={handleDateChange}
                                                onClean={() => {
                                                    setFromDate('');
                                                    setToDate('');
                                                    setReportrange('');
                                                }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 col-xl-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary w-100"
                                                onClick={handleSearch}
                                                disabled={loading}
                                            >
                                                {loading ? 'Loading...' : 'Search'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="account-ledger-results">
                            {message ? (
                                <div className="alert alert-warning mt-3" role="alert">
                                    {message}
                                </div>
                            ) : null}
                            {periodLabel ? (
                                <div className="mt-3 mb-2">
                                    <strong>Sales Ledger (Double Entry)</strong>
                                    <span className="ms-3">Period: {periodLabel}</span>
                                </div>
                            ) : null}
                            {periodLabel ? (
                                <div className="verical-scrl-wrapper common-table account-ledger-summary mb-3">
                                    <table className="table table-bordered dataTable">
                                        <thead>
                                            <tr>
                                                <th>Total Debit</th>
                                                <th>Total Credit</th>
                                                <th>Difference</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="text-end">{summary.total_debit}</td>
                                                <td className="text-end">{summary.total_credit}</td>
                                                <td className="text-end">{summary.difference}</td>
                                                <td>
                                                    {summary.is_balanced ? 'Balanced' : 'Not Balanced'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                            <div className="verical-scrl-wrapper common-table account-ledger-table-scroll">
                                <table className="table table-bordered dataTable">
                                    <thead>
                                        <tr>
                                            <th>Sr. No.</th>
                                            <th>Date</th>
                                            <th>Voucher / Ref No.</th>
                                            <th>Type</th>
                                            <th>Account</th>
                                            <th>Particulars</th>
                                            <th className="text-end">Debit</th>
                                            <th className="text-end">Credit</th>
                                            <th className="text-end">Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ledgerRows.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" className="text-center">
                                                    No ledger entries. Click Search to load.
                                                </td>
                                            </tr>
                                        ) : (
                                            ledgerRows.map((row, index) => (
                                                <tr key={`${row.voucher_id}-${row.account_name}-${index}`}>
                                                    <td>{index + 1}</td>
                                                    <td>{row.date}</td>
                                                    <td>{row.voucher_no}</td>
                                                    <td>{row.entry_type}</td>
                                                    <td>{row.account_name}</td>
                                                    <td>{row.particulars}</td>
                                                    <td className="text-end">{row.debit}</td>
                                                    <td className="text-end">{row.credit}</td>
                                                    <td className="text-end">{row.balance}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default SalesLedger;
